// src/screens/RideTypes.jsx
import "bootstrap-icons/font/bootstrap-icons.css";
import { useEffect, useMemo, useState, memo } from "react";
import Swal from "sweetalert2";
import { useLogs } from "../context/LogContext";
import "../css/RideTypes.css";
import useIdleLogout from "../hooks/useIdleLogout";

/* ===================== ENV / ENDPOINTS ===================== */
/**
 * Your .env:
 * REACT_APP_RIDETYPES_API_GET_URL=https://backend.tabdhey.bt/grablike/api/get-ride-types
 * REACT_APP_RIDETYPES_API_ADD_URL=https://backend.tabdhey.bt/grablike/api/add-ride-types
 * REACT_APP_RIDETYPES_API_UPDATE_URL=https://backend.tabdhey.bt/grablike/api/edit-ride-type/:id
 * REACT_APP_RIDETYPES_API_DELETE_URL=https://backend.tabdhey.bt/grablike/api/hard-delete-ride-type/:id
 *
 * OPTIONAL (recommended for image fixing):
 * REACT_APP_RIDES_FILE_BASE_URL=https://backend.tabdhey.bt  (or your CDN base)
 * REACT_APP_POSTIMG_BASE=https://i.postimg.cc               (optional; default used if missing)
 */
const ENV_GET = (process.env.REACT_APP_RIDETYPES_API_GET_URL || "").trim();
const ENV_ADD = (process.env.REACT_APP_RIDETYPES_API_ADD_URL || "").trim();
const ENV_UPDATE = (process.env.REACT_APP_RIDETYPES_API_UPDATE_URL || "").trim();
const ENV_DELETE = (process.env.REACT_APP_RIDETYPES_API_DELETE_URL || "").trim();

// Optional file base if backend returns relative URLs (example: /uploads/xxx.jpg)
const FILE_BASE_RIDES = (process.env.REACT_APP_RIDES_FILE_BASE_URL || "").replace(
  /\/+$/,
  ""
);

// ✅ If backend sends postimg path only like "/ZnMdw0xQ/taxi_Reserved.jpg"
const POSTIMG_BASE = (
  process.env.REACT_APP_POSTIMG_BASE || "https://i.postimg.cc"
).replace(/\/+$/, "");

/* ---------- Min Fare floor from env (optional) ---------- */
const MF_FLOOR = Number(process.env.REACT_APP_MINFARE_FLOOR || 0);

/* Derive a base like http://host:port/api from a full URL */
function deriveBase(u) {
  try {
    const url = new URL(u);
    const parts = url.pathname.split("/").filter(Boolean);
    parts.pop();
    const path = "/" + parts.join("/");
    return `${url.origin}${path}`.replace(/\/+$/, "");
  } catch {
    return "";
  }
}

const API_BASE =
  (process.env.REACT_APP_RIDETYPES_API_BASE || "").trim() ||
  deriveBase(ENV_ADD) ||
  deriveBase(ENV_GET) ||
  "";

// Defaults match your new backend (only used if env missing)
const GET_URL = ENV_GET || (API_BASE ? `${API_BASE}/getall` : "");
const ADD_URL = ENV_ADD || (API_BASE ? `${API_BASE}/` : "");
const UPDATE_URL = ENV_UPDATE || (API_BASE ? `${API_BASE}/:id` : "");
const DELETE_URL = ENV_DELETE || (API_BASE ? `${API_BASE}/:ride_type_id` : "");

const PLACEHOLDER_IMG = "/assets/img/placeholder.png";

/* ===================== HELPERS ===================== */
const toAbsUrl = (raw) => {
  if (!raw) return "";

  // already absolute
  if (/^(data:|blob:|https?:\/\/)/i.test(raw)) return raw;

  // ensure starts with "/"
  const path = raw.startsWith("/") ? raw : `/${raw}`;

  /**
   * ✅ FIX: If backend returns postimg path only like:
   *   "/ZnMdw0xQ/taxi_Reserved.jpg"
   * build it as:
   *   "https://i.postimg.cc/ZnMdw0xQ/taxi_Reserved.jpg"
   */
  const looksLikePostImgPath = /^\/[A-Za-z0-9]+\/.+\.(png|jpe?g|webp|gif|svg)$/i.test(
    path
  );
  if (looksLikePostImgPath) return `${POSTIMG_BASE}${path}`;

  // If you have your own file server base (cdn/api domain)
  if (FILE_BASE_RIDES) return `${FILE_BASE_RIDES}${path}`;

  // fallback same-origin
  return path;
};

/* ✅ Access token from login storage (for Authorization: Bearer <ACCESS_TOKEN>) */
function getAccessToken() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return (
      localStorage.getItem("access_token") ||
      localStorage.getItem("token") ||
      u?.access_token ||
      u?.token ||
      u?.jwt ||
      ""
    );
  } catch {
    return (
      localStorage.getItem("access_token") || localStorage.getItem("token") || ""
    );
  }
}

/* ✅ Build auth headers once */
function authHeaders(extra = {}) {
  const token = getAccessToken();
  return {
    ...extra,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// Build URLs that may contain :id or :ride_type_id
function buildUrl(template, id, token = ":id") {
  if (!template) return "";
  if (template.includes(token)) {
    return template.replace(token, encodeURIComponent(id));
  }
  return `${template.replace(/\/+$/, "")}/${encodeURIComponent(id)}`;
}

const isNum = (v) => v === "" || /^-?\d*\.?\d*$/.test(v);
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Compute min fare: Base + (Per Km × 1), then apply optional floor */
function computeMinFare(base, perKm, floor = MF_FLOOR) {
  const coverage = (Number(base) || 0) + (Number(perKm) || 0) * 1;
  return round2(Math.max(coverage, Number(floor) || 0));
}

/** Stable key generator (no index) */
const rowKey = (r, fallbackIdx) => {
  const base =
    r.id ??
    r.ride_type_id ??
    r._id ??
    r.code ??
    `${r.name ?? "ride"}-${r.vehicle_type ?? "veh"}`;
  return String(base ?? `rt-${fallbackIdx}`);
};

/** normalize for case/space-insensitive comparisons */
const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();

/** check for similar/duplicate rows; returns an array of messages */
function findConflicts(rows, candidate, excludeId = null) {
  const msgs = [];
  const nName = norm(candidate.name);
  const nCode = norm(candidate.code);
  const nVeh = norm(candidate.vehicle_type);

  for (const r of rows) {
    if (excludeId != null && String(r.id) === String(excludeId)) continue;

    const rName = norm(r.name);
    const rCode = norm(r.code);
    const rVeh = norm(r.vehicle_type);

    if (nName && rName && nName === rName) {
      msgs.push(
        `Another ride with the same <b>Name</b> (<i>${r.name}</i>) already exists.`
      );
    }
    if (nCode && rCode && nCode === rCode) {
      msgs.push(
        `Another ride with the same <b>Code</b> (<i>${r.code}</i>) already exists.`
      );
    }
    if (nName && rName && nVeh && rVeh && nName === rName && nVeh === rVeh) {
      msgs.push(
        `The pair <b>Name + Vehicle</b> (<i>${r.name}</i>, <i>${r.vehicle_type}</i>) already exists.`
      );
    }
    if (nName === rName && nCode === rCode && nVeh === rVeh) {
      msgs.push(
        `Exact duplicate of <i>${r.name}</i> (${r.code}, ${r.vehicle_type}).`
      );
    }
  }

  return Array.from(new Set(msgs));
}

/** delivery name? (starts with OR ends with "delivery", case-insensitive) */
const isDeliveryName = (name) => {
  const s = String(name || "").trim().toLowerCase();
  return s.startsWith("delivery") || s.endsWith("delivery");
};

/** generate a unique Delivery code: D0000..D1000 (inclusive) */
function generateDeliveryCode(existingCodesSet) {
  for (let i = 0; i < 2000; i++) {
    const n = Math.floor(Math.random() * 1001); // 0..1000
    const candidate = `D${String(n).padStart(4, "0")}`;
    if (!existingCodesSet.has(candidate.toLowerCase())) return candidate;
  }
  return null;
}

/* ===================== ROW (memoized) ===================== */
const TableRow = memo(function TableRow({
  r,
  i,
  editingId,
  editRow,
  editAutoMin,
  editIconFile,
  onEditField,
  onEditFileChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDeleteRow,
}) {
  const isEditing = editingId === r.id;
  const safeKey = rowKey(r, i);

  return (
    <tr key={safeKey}>
      <td>{i + 1}</td>

      {/* Icon */}
      <td className="text-center">
        {isEditing ? (
          <div className="d-flex flex-column gap-1">
            <input
              type="file"
              accept="image/*"
              className="form-control form-control-sm w-100"
              onChange={(e) => onEditFileChange(e.target.files[0] || null)}
            />
            <input
              className="form-control form-control-sm w-100"
              style={{ minWidth: 160 }}
              value={editRow.icon_url}
              onChange={(e) => onEditField("icon_url", e.target.value)}
              placeholder="Or paste URL"
            />
            {editIconFile && (
              <small className="text-success">{editIconFile.name}</small>
            )}
          </div>
        ) : (
          <div className="thumb-wrap">
            <img
              // ✅ FIX: always convert to absolute
              src={toAbsUrl(r.icon_url) || PLACEHOLDER_IMG}
              alt=""
              className="thumb"
              width={64}
              height={64}
              loading="lazy"
              decoding="async"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = PLACEHOLDER_IMG;
              }}
            />
          </div>
        )}
      </td>

      {/* name */}
      <td>
        {isEditing ? (
          <input
            className="form-control form-control-sm w-100"
            style={{ minWidth: 160 }}
            value={editRow.name}
            onChange={(e) => onEditField("name", e.target.value)}
          />
        ) : (
          r.name
        )}
      </td>

      {/* code */}
      <td>
        {isEditing ? (
          <input
            className="form-control form-control-sm w-100"
            style={{ minWidth: 120 }}
            value={editRow.code}
            onChange={(e) => onEditField("code", e.target.value)}
          />
        ) : (
          r.code
        )}
      </td>

      {/* description */}
      <td>
        {isEditing ? (
          <input
            className="form-control form-control-sm w-100"
            style={{ minWidth: 220 }}
            value={editRow.description}
            onChange={(e) => onEditField("description", e.target.value)}
          />
        ) : (
          r.description
        )}
      </td>

      {/* base fare */}
      <td>
        {isEditing ? (
          <input
            className="form-control form-control-sm w-100"
            style={{ minWidth: 110 }}
            value={editRow.base_fare}
            onChange={(e) => onEditField("base_fare", e.target.value)}
          />
        ) : (
          Number(r.base_fare).toFixed(2)
        )}
      </td>

      {/* per km */}
      <td>
        {isEditing ? (
          <input
            className="form-control form-control-sm w-100"
            style={{ minWidth: 110 }}
            value={editRow.per_km_rate}
            onChange={(e) => onEditField("per_km_rate", e.target.value)}
          />
        ) : (
          Number(r.per_km_rate).toFixed(2)
        )}
      </td>

      {/* min fare */}
      <td>
        {isEditing ? (
          <div className="d-flex align-items-center gap-2">
            <input
              className="form-control form-control-sm w-100"
              style={{ minWidth: 110 }}
              value={editRow.min_fare}
              onChange={(e) => onEditField("min_fare", e.target.value)}
              disabled={editAutoMin}
            />
            <div className="form-check form-switch m-0 d-inline-flex align-items-center gap-2">
              <input
                className="form-check-input"
                type="checkbox"
                id={`autoMin-${safeKey}`}
                checked={editAutoMin}
                onChange={() => onEditField("__toggle_auto__", null)}
                aria-label="Auto-compute min fare"
                title="Auto compute from Base and Per-Km"
              />
              <span className="small">Auto</span>
            </div>
          </div>
        ) : (
          Number(r.min_fare).toFixed(2)
        )}
      </td>

      {/* cancel fee */}
      <td>
        {isEditing ? (
          <input
            className="form-control form-control-sm w-100"
            style={{ minWidth: 110 }}
            value={editRow.cancellation_fee}
            onChange={(e) => onEditField("cancellation_fee", e.target.value)}
          />
        ) : (
          Number(r.cancellation_fee).toFixed(2)
        )}
      </td>

      {/* vehicle */}
      <td>
        {isEditing ? (
          <input
            className="form-control form-control-sm w-100"
            style={{ minWidth: 120 }}
            value={editRow.vehicle_type}
            onChange={(e) => onEditField("vehicle_type", e.target.value)}
          />
        ) : (
          r.vehicle_type
        )}
      </td>

      {/* active */}
      <td className="text-center">
        {isEditing ? (
          <input
            type="checkbox"
            className="form-check-input"
            checked={!!editRow.is_active}
            onChange={() => onEditField("is_active", !editRow.is_active)}
          />
        ) : (
          <span className="status-icon-wrap">
            {r.is_active ? (
              <i className="bi bi-check2-circle text-success" />
            ) : (
              <i className="bi bi-x-circle text-danger" />
            )}
          </span>
        )}
      </td>

      {/* actions */}
      <td className="actions-cell">
        {isEditing ? (
          <>
            <button
              type="button"
              className="btn btn-confirm me-2"
              title="Save"
              onClick={() => onSaveEdit(r.id)}
            >
              <i className="bi bi-check-lg" />
            </button>
            <button
              type="button"
              className="btn btn-cancel"
              title="Cancel"
              onClick={onCancelEdit}
            >
              <i className="bi bi-x-lg" />
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-view me-2"
              title="Edit"
              onClick={() => onStartEdit(r)}
            >
              <i className="bi bi-pencil" />
            </button>
            <button
              type="button"
              className="btn btn-delete"
              title="Delete"
              onClick={() => onDeleteRow(r.id)}
            >
              <i className="bi bi-trash" />
            </button>
          </>
        )}
      </td>
    </tr>
  );
});

/* ===================== COMPONENT ===================== */
export default function RideTypes() {
  useIdleLogout();
  const { logActivity } = useLogs();

  // form state
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [baseFare, setBaseFare] = useState("");
  const [perKmRate, setPerKmRate] = useState("");
  const [cancellationFee, setCancellationFee] = useState("");
  const [capacity, setCapacity] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [iconUrl, setIconUrl] = useState("");
  const [iconFile, setIconFile] = useState(null);
  const [isActive, setIsActive] = useState(true);

  // computed min fare (AUTO)
  const [computedMinFare, setComputedMinFare] = useState(0);

  // ui state
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [fadeOut, setFadeOut] = useState(false);

  // table/edit state
  const [rideTypes, setRideTypes] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editRow, setEditRow] = useState({});
  const [origRow, setOrigRow] = useState({});
  const [editAutoMin, setEditAutoMin] = useState(true);
  const [editIconFile, setEditIconFile] = useState(null);

  /* ---------- flash message auto-hide ---------- */
  useEffect(() => {
    if (message) {
      setFadeOut(false);
      const t1 = setTimeout(() => setFadeOut(true), 1500);
      const t2 = setTimeout(() => setMessage(null), 2200);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [message]);

  /* ---------- fetch list ---------- */
  const fetchRideTypes = async () => {
    if (!GET_URL) {
      console.warn("GET URL not configured.");
      return;
    }
    try {
      const res = await fetch(GET_URL, {
        headers: authHeaders(), // ✅ Bearer token added
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const payload = await res.json();

      const list = Array.isArray(payload) ? payload : payload?.data ?? [];
      const rows = list.map((item) => {
        const id = item.id || item.ride_type_id || item._id || item.code;
        return {
          id,
          name: item.name ?? "",
          code: item.code ?? "",
          description: item.description ?? "",
          base_fare: item.base_fare ?? 0,
          per_km_rate: item.per_km_rate ?? 0,
          min_fare: item.min_fare ?? 0,
          cancellation_fee: item.cancellation_fee ?? 0,
          capacity: item.capacity ?? 0,
          vehicle_type: item.vehicle_type ?? "Car",
          // ✅ FIX: convert backend path OR url to absolute
          icon_url: toAbsUrl(item.icon_url || item.image || item.icon),
          is_active: Boolean(item.is_active ?? true),
        };
      });

      setRideTypes(rows);
    } catch (err) {
      console.error("Failed to fetch ride types", err);
      setMessage({ type: "error", text: "Could not load ride types." });
    }
  };

  useEffect(() => {
    fetchRideTypes();
    // eslint-disable-next-line
  }, []);

  /* ---------- recompute (Create form) ---------- */
  useEffect(() => {
    setComputedMinFare(computeMinFare(baseFare, perKmRate));
  }, [baseFare, perKmRate]);

  /* ---------- AUTO code logic (Create form) ---------- */
  useEffect(() => {
    if (!isDeliveryName(name)) return;

    const existing = new Set(
      rideTypes.map((r) => String(r.code || "").toLowerCase()).filter(Boolean)
    );

    const codeLC = String(code || "").toLowerCase();
    const validPattern = /^d\d{4}$/.test(codeLC);

    if (!validPattern || existing.has(codeLC)) {
      const gen = generateDeliveryCode(existing);
      if (gen && gen !== code) setCode(gen);
    }
  }, [name, rideTypes, code]);

  /* ---------- numeric setters ---------- */
  const setNum = (setter) => (e) => {
    const v = e.target.value;
    if (isNum(v)) setter(v);
  };

  /* ---------- submit create ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ADD_URL) {
      Swal.fire("Missing config", "ADD URL not configured in .env", "error");
      return;
    }

    let finalCode = code.trim();
    if (isDeliveryName(name)) {
      const existing = new Set(
        rideTypes.map((r) => String(r.code || "").toLowerCase()).filter(Boolean)
      );
      const needGen =
        !/^d\d{4}$/i.test(finalCode) || existing.has(finalCode.toLowerCase());

      if (needGen) {
        const gen = generateDeliveryCode(existing);
        if (!gen) {
          Swal.fire(
            "Code Error",
            "Could not auto-generate a unique Delivery code.",
            "error"
          );
          return;
        }
        finalCode = gen;
        setCode(gen);
      }
    }

    const body = {
      name: name.trim(),
      code: finalCode,
      description: description.trim(),
      base_fare: parseFloat(baseFare) || 0,
      per_km_rate: parseFloat(perKmRate) || 0,
      min_fare: computedMinFare,
      cancellation_fee: parseFloat(cancellationFee) || 0,
      capacity: parseInt(capacity || "0", 10) || 0,
      vehicle_type: (vehicleType || "Car").trim(),
      icon_url: iconUrl.trim(),
      is_active: isActive ? 1 : 0,
    };

    const conflicts = findConflicts(rideTypes, body, null);
    if (conflicts.length) {
      Swal.fire({
        icon: "warning",
        title: "Duplicate / Similar Entry",
        html: conflicts.map((m) => `<div>${m}</div>`).join(""),
      });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);

      const fd = new FormData();
      Object.entries(body).forEach(([k, v]) => fd.append(k, String(v)));
      if (iconFile) fd.append("icon", iconFile);
      const res = await fetch(ADD_URL, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      await res.json();

      logActivity?.(`Added ride type "${body.name}"`);

      Swal.fire({
        icon: "success",
        title: "Ride Type Added!",
        text: `"${body.name}" added successfully.`,
        timer: 1800,
        showConfirmButton: false,
      });

      setMessage({ type: "success", text: "Ride Type added successfully!" });

      setName("");
      setCode("");
      setDescription("");
      setBaseFare("");
      setPerKmRate("");
      setCancellationFee("");
      setCapacity("");
      setVehicleType("");
      setIconUrl("");
      setIconFile(null);
      setIsActive(true);
      setComputedMinFare(0);

      fetchRideTypes();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.message || "Something went wrong.", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ---------- row edit ---------- */
  const startEdit = (r) => {
    setEditingId(r.id);
    const init = {
      name: String(r.name ?? ""),
      code: String(r.code ?? ""),
      description: String(r.description ?? ""),
      base_fare: String(r.base_fare ?? ""),
      per_km_rate: String(r.per_km_rate ?? ""),
      min_fare: String(r.min_fare ?? ""),
      cancellation_fee: String(r.cancellation_fee ?? ""),
      capacity: String(r.capacity ?? ""),
      vehicle_type: String(r.vehicle_type ?? "Car"),
      icon_url: r.icon_url || "",
      is_active: Boolean(r.is_active),
    };
    setEditRow(init);

    const computed = computeMinFare(r.base_fare, r.per_km_rate);
    setEditAutoMin(Math.abs(Number(r.min_fare) - computed) < 0.01);

    setOrigRow(init);
    setEditIconFile(null);
  };

  const onEditField = (field, value) => {
    if (field === "__toggle_auto__") {
      setEditAutoMin((v) => !v);
      return;
    }
    const numericFields = [
      "base_fare",
      "per_km_rate",
      "min_fare",
      "cancellation_fee",
      "capacity",
    ];
    if (numericFields.includes(field) && !isNum(value)) return;
    setEditRow((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (!editingId || !editAutoMin) return;
    setEditRow((prev) => ({
      ...prev,
      min_fare: String(computeMinFare(prev.base_fare, prev.per_km_rate)),
    }));
  }, [editingId, editAutoMin, editRow.base_fare, editRow.per_km_rate]);

  const saveEdit = async (id) => {
    if (!UPDATE_URL) {
      Swal.fire("Missing config", "UPDATE URL not configured in .env", "error");
      return;
    }

    const body = {
      name: (editRow.name || "").trim(),
      code: (editRow.code || "").trim(),
      description: (editRow.description || "").trim(),
      base_fare: parseFloat(editRow.base_fare) || 0,
      per_km_rate: parseFloat(editRow.per_km_rate) || 0,
      min_fare: parseFloat(editRow.min_fare) || 0,
      cancellation_fee: parseFloat(editRow.cancellation_fee) || 0,
      capacity: parseInt(editRow.capacity || "0", 10) || 0,
      vehicle_type: (editRow.vehicle_type || "Car").trim(),
      icon_url: (editRow.icon_url || "").trim(),
      is_active: editRow.is_active ? 1 : 0,
    };

    const conflicts = findConflicts(rideTypes, body, id);
    if (conflicts.length) {
      Swal.fire({
        icon: "warning",
        title: "Duplicate / Similar Entry",
        html: conflicts.map((m) => `<div>${m}</div>`).join(""),
      });
      return;
    }

    try {
      setLoading(true);
      const url = buildUrl(UPDATE_URL, id, ":id");

      const fd = new FormData();
      Object.entries(body).forEach(([k, v]) => fd.append(k, String(v)));
      if (editIconFile) fd.append("icon", editIconFile);
      const res = await fetch(url, {
        method: "PUT",
        headers: authHeaders(),
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      await res.json();

      logActivity?.(`Updated ride type "${body.name}"`);
      Swal.fire("Updated!", "Ride type updated successfully!", "success");
      setEditingId(null);
      setEditRow({});
      setOrigRow({});
      setEditIconFile(null);
      fetchRideTypes();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.message || "Could not update ride type.", "error");
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    setEditRow(origRow);
    setEditingId(null);
    setOrigRow({});
    setEditIconFile(null);
  };

  const deleteRow = async (id) => {
    if (!DELETE_URL) {
      Swal.fire("Missing config", "DELETE URL not configured in .env", "error");
      return;
    }

    const rideName = rideTypes.find((r) => r.id === id)?.name || "";

    const { isConfirmed } = await Swal.fire({
      title: "Are you sure?",
      text: "This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    });
    if (!isConfirmed) return;

    try {
      const url = buildUrl(DELETE_URL, id, ":id");

      const res = await fetch(url, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(await res.text());
      await res.json();

      logActivity?.(`Deleted ride type "${rideName}"`);
      Swal.fire("Deleted!", "Ride type removed.", "success");
      fetchRideTypes();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.message || "Could not delete ride type.", "error");
    }
  };

  const memoRows = useMemo(() => rideTypes, [rideTypes]);
  const deliveryName = isDeliveryName(name);

  return (
    <>
      {/* Create form */}
      <div className="card mb-4">
        <div className="card-body">
          {message && (
            <div
              className={`alert alert-${message.type} ${
                fadeOut ? "fade-out" : ""
              }`}
            >
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Ride Name</label>
              <input
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder=" "
                required
              />
            </div>

            <div className="col-md-4">
              <label className="form-label d-flex align-items-center gap-2">
                Code
                {deliveryName && (
                  <span className="badge bg-success-subtle text-success border border-success-subtle">
                    AUTO (Delivery)
                  </span>
                )}
              </label>
              <input
                className="form-control"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={
                  deliveryName ? "Auto (D0000–D1000)" : "Enter a unique code"
                }
                disabled={deliveryName}
                required
              />
              {!deliveryName && (
                <small className="text-muted">
                  For non-Delivery rides, enter a unique code.
                </small>
              )}
            </div>

            <div className="col-md-4">
              <label className="form-label">Vehicle Type</label>
              <input
                className="form-control"
                value={vehicleType}
                onChange={(e) => setVehicleType(e.target.value)}
              />
            </div>

            <div className="col-md-8">
              <label className="form-label">Description</label>
              <input
                className="form-control"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Icon</label>
              <input
                type="file"
                accept="image/*"
                className="form-control"
                onChange={(e) => {
                  const f = e.target.files[0] || null;
                  setIconFile(f);
                  if (f) setIconUrl("");
                }}
              />
              <input
                className="form-control mt-1"
                value={iconUrl}
                onChange={(e) => {
                  setIconUrl(e.target.value);
                  if (e.target.value) setIconFile(null);
                }}
                placeholder="Or paste Icon URL"
              />
              {iconFile && (
                <small className="text-success">{iconFile.name}</small>
              )}
            </div>

            <div className="col-md-3">
              <label className="form-label">Base Fare</label>
              <input
                className="form-control"
                value={baseFare}
                onChange={setNum(setBaseFare)}
                placeholder="0"
                required
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Per Km Rate</label>
              <input
                className="form-control"
                value={perKmRate}
                onChange={setNum(setPerKmRate)}
                placeholder="0"
                required
              />
            </div>

            {/* AUTO Min Fare */}
            <div className="col-md-3">
              <label className="form-label d-flex align-items-center gap-2">
                Min Fare
                <span className="badge bg-success-subtle text-success border border-success-subtle">
                  AUTO
                </span>
              </label>
              <input
                className="form-control"
                value={String(
                  computedMinFare?.toFixed
                    ? computedMinFare.toFixed(2)
                    : computedMinFare
                )}
                readOnly
                disabled
              />
              <small className="text-muted">
                = Base + (Per Km × 1)
                {MF_FLOOR > 0 ? `, floor BTN ${MF_FLOOR}` : ""}
              </small>
            </div>

            <div className="col-md-3">
              <label className="form-label">Cancellation Fee</label>
              <input
                className="form-control"
                value={cancellationFee}
                onChange={setNum(setCancellationFee)}
                placeholder="0"
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Capacity</label>
              <input
                className="form-control"
                value={capacity}
                onChange={setNum(setCapacity)}
                placeholder="0"
              />
            </div>

            <div className="col-md-3 d-flex align-items-end">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="activeSwitch"
                  checked={isActive}
                  onChange={() => setIsActive((v) => !v)}
                />
                <label className="form-check-label" htmlFor="activeSwitch">
                  Active
                </label>
              </div>
            </div>

            <div className="col-12">
              <button
                type="submit"
                className="add-ride-type-btn"
                disabled={loading}
              >
                {loading ? "Saving…" : "Add Ride Type"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Table */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">All Ride Types</h5>
          <div style={{ height: 0 }} />
        </div>

        <div className="card-body table-responsive ride-types-table-card">
          {memoRows.length === 0 ? (
            <p className="text-muted m-0">No ride types added yet.</p>
          ) : (
            <table className="table table-bordered align-middle table-nowrap rt-no-anim">
              {/* ✅ YOUR ORIGINAL WIDTHS — NOT CHANGED */}
              <colgroup>
                <col style={{ width: "60px" }} />
                <col style={{ width: "290px" }} />
                <col style={{ width: "220px" }} />
                <col style={{ width: "140px" }} />
                <col style={{ width: "240px" }} />
                <col style={{ width: "140px" }} />
                <col style={{ width: "140px" }} />
                <col style={{ width: "180px" }} />
                <col style={{ width: "140px" }} />
                <col style={{ width: "140px" }} />
                <col style={{ width: "100px" }} />
                <col style={{ width: "160px" }} />
              </colgroup>

              <thead className="table-light">
                <tr>
                  <th>SI.No</th>
                  <th>Icon</th>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Base Fare</th>
                  <th>Per Km</th>
                  <th>Min Fare</th>
                  <th>Cancel Fee</th>
                  <th>Vehicle</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {memoRows.map((r, i) => (
                  <TableRow
                    key={rowKey(r, i)}
                    r={r}
                    i={i}
                    editingId={editingId}
                    editRow={editRow}
                    editAutoMin={editAutoMin}
                    editIconFile={editIconFile}
                    onEditField={onEditField}
                    onEditFileChange={setEditIconFile}
                    onStartEdit={startEdit}
                    onSaveEdit={saveEdit}
                    onCancelEdit={cancelEdit}
                    onDeleteRow={deleteRow}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <footer className="dashboard-footer">
        <small>
          © {new Date().getFullYear()}{" "}
          <strong>སྟབས་བདེ། </strong> Admin • All rights reserved
        </small>
      </footer>
    </>
  );
}
